import { undoDepth } from "@codemirror/commands";
import type { EditorView } from "@codemirror/view";
import {
  type FC,
  forwardRef,
  type MutableRefObject,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Spinner } from "react-bootstrap";

import { CodeEditor } from "../../../../components/CMEditor/CodeEditor";
import type { IChoiceState } from "./useChoicesState";

export interface IPromptJsonEditorProps {
  initialValue: string;
  isLoading: boolean;
  onSave: (value: any) => void;
  setHasChanges?: (value: boolean) => void;
  answerData: IChoiceState;
  ref?: any;
  withBanner: boolean;
}

const AnswerJsonEditor: FC<IPromptJsonEditorProps> = forwardRef(
  (
    { initialValue, isLoading, onSave, setHasChanges, answerData, withBanner },
    ref,
  ) => {
    const [isError, setIsError] = useState(false);
    const [updatedAnswer, setUpdatedAnswer] = useState<string | undefined>("");
    const [isDisabled, setIsDisabled] = useState(false);
    const [editorReady, setEditorReady] = useState(false);

    const restoreState = () => {
      if (ref) {
        const editorState = (ref as MutableRefObject<EditorView>).current;
        if (editorState && answerData.jsonState) {
          editorState.setState(answerData.jsonState);
        }
      }
    };

    useEffect(() => {
      if (editorReady) {
        restoreState();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorReady]);

    useEffect(() => {
      if (answerData.jsonUnsavedChanges !== null) {
        setTimeout(() => {
          restoreState();
        });
      }
      setUpdatedAnswer(initialValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answerData, initialValue]);

    useEffect(() => {
      setTimeout(() => {
        const disableUndo = (ref as MutableRefObject<EditorView>).current?.state
          ? undoDepth((ref as any).current.state) === 0
          : true;
        setHasChanges && setHasChanges(!disableUndo);
        setIsDisabled(disableUndo || isError);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedAnswer, setHasChanges, isError, editorReady]);

    const handleOnChange = (e: string) => {
      if (!e) {
        setUpdatedAnswer("");
        return;
      }
      setUpdatedAnswer(JSON.stringify(JSON.parse(e)));
    };

    const handleError = (value: boolean) => {
      setIsError(value);
    };

    const handleClickSave = () => {
      onSave(updatedAnswer);
    };

    const initialValueObject = useMemo(() => {
      if (!initialValue) return;
      try {
        return JSON.stringify(JSON.parse(initialValue), null, 2);
      } catch (e) {
        return undefined;
      }
    }, [initialValue]);

    return (
      <div data-testid="json-editor">
        <CodeEditor
          initialValue={initialValueObject}
          onChange={handleOnChange}
          onError={handleError}
          withToolbar
          withType
          minHeight="200px"
          placeholder="Enter your JSON here"
          ref={ref}
          setEditorReady={setEditorReady}
          withBanner={withBanner}
        />
        <div className="mt-5 d-flex justify-content-end gap-5 ">
          <Button
            onClick={handleClickSave}
            variant="primary"
            disabled={isDisabled || !updatedAnswer}
          >
            Save
            {isLoading && (
              <Spinner className="ms-2" size="sm" animation="border" />
            )}
          </Button>
        </div>
      </div>
    );
  },
);

export { AnswerJsonEditor };
